import React, { FC, useState } from 'react';
import { MenuItem, TextField, Box } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Controller, UseFormReturn } from 'react-hook-form';
import { ProjectedPaymentsForm } from '../services';
import { TFunction } from 'i18next';
import TooltipInput from './tooltip-input';
import InputCell from './input-cell';

const useStyles = makeStyles((theme: any) => ({
  root: {
    display: 'flex',
    height: 'auto',
    '&>*': {
      marginRight: theme.spacing(4),
      height: 'auto !important',
      flex: 1,
    },
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: theme.spacing(1),
      '&>*': { marginRight: 0, width: '100%', minWidth: 0, flex: 'none' },
    },
  },
  textField: {
    maxWidth: '50%',
    [theme.breakpoints.down('sm')]: { maxWidth: 'none' },
    height: theme.spacing(4),
    '&>.MuiInputBase-root': {
      height: theme.spacing(4),
      '&>.MuiSelect-select': {
        paddingTop: theme.spacing(5),
        paddingBottom: theme.spacing(5),
      },
    },
    '@media (hover: none) and (pointer: coarse)': {
      height: 'auto !important',
      '&&>.MuiInputBase-root': { height: 'auto', minHeight: 44 },
      '&&>.MuiInputBase-root>.MuiSelect-select': {
        paddingTop: theme.spacing(1.5),
        paddingBottom: theme.spacing(1.5),
      },
    },
  },
  rangeAmounts: {
    height: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
}));

type PrincipalInterestInputProps = {
  t: TFunction;
  methods: UseFormReturn<ProjectedPaymentsForm>;
  onEditPrincipalInterest: () => void;
  calculatedPrincipalInterestValue?: number;
  disabled?: boolean;
};

const PrincipalInterestInput: FC<PrincipalInterestInputProps> = ({
  t,
  methods,
  onEditPrincipalInterest,
  calculatedPrincipalInterestValue,
  disabled = false,
}) => {
  const classes = useStyles();
  const { watch, control } = methods;
  const principalKind = watch('principal_kind');

  const data = [
    {
      name: 'amount',
      text: t('projectedPayments:amount'),
    },
    {
      name: 'range',
      text: t('projectedPayments:range'),
    },
  ];

  return (
    <Box className={classes.root}>
      <Controller
        control={control}
        name="principal_kind"
        render={({ field }) => (
          <TextField
            select
            fullWidth
            disabled={disabled}
            variant="filled"
            value={field.value}
            className={classes.textField}
            InputProps={{ disableUnderline: true }}
            inputProps={{ 'aria-label': t('projectedPayments:principalInterest') }}
            onChange={(e) => field.onChange(e)}
          >
            {data.map((element: any) => (
              <MenuItem key={element.name} value={element.name}>
                {element.text}
              </MenuItem>
            ))}
          </TextField>
        )}
      />

      {principalKind === 'amount' && (
        <TooltipInput
          t={t}
          onEditMortgage={onEditPrincipalInterest}
          methods={methods}
          name="principal_amount"
          calculatedValue={calculatedPrincipalInterestValue}
        />
      )}

      {principalKind === 'range' && (
        <Box className={classes.rangeAmounts}>
          <Controller
            control={control}
            name="principal_min"
            render={({ field }) => (
              <InputCell
                placeholder={t('projectedPayments:min')}
                value={field.value}
                ariaLabel={t('projectedPayments:min')}
                onChangeInput={(e) => field.onChange(e)}
              />
            )}
          />

          <Controller
            control={control}
            name="principal_max"
            render={({ field }) => (
              <InputCell
                placeholder={t('projectedPayments:max')}
                value={field.value}
                ariaLabel={t('projectedPayments:max')}
                onChangeInput={(e) => field.onChange(e)}
              />
            )}
          />
        </Box>
      )}
    </Box>
  );
};

export default PrincipalInterestInput;
